'use client';

/**
 * Zustand store for the Suggest-outcome wizard.
 *
 * The suggest-outcome flow is manual-only: users provide label, ticker,
 * image, and an optional reason. AI single-outcome generation was removed
 * with the old AI image pipeline.
 */
import type { JSX, ReactNode } from 'react';
import { type Context, createContext, createElement, useContext } from 'react';
import { type StoreApi, type UseBoundStore, useStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
export type WizardStep = 1 | 2;
interface SuggestOutcomeWizardState {
  step: WizardStep;
  label: string;
  ticker: string;
  imageUrl: string;
  imageQuery: string | null;
  reason: string;
  setStep: (step: WizardStep) => void;
  setLabel: (label: string) => void;
  setTicker: (ticker: string) => void;
  setImage: (imageUrl: string, imageQuery: string | null) => void;
  removeImage: () => void;
  setReason: (reason: string) => void;
  reset: () => void;
}
type PersistedState = Pick<SuggestOutcomeWizardState, 'step' | 'label' | 'ticker' | 'imageUrl' | 'imageQuery' | 'reason'>;
const INITIAL: PersistedState = {
  step: 1,
  label: '',
  ticker: '',
  imageUrl: '',
  imageQuery: null,
  reason: ''
};
type WizardBoundStore = UseBoundStore<StoreApi<SuggestOutcomeWizardState>>;
function createSuggestOutcomeWizardStore(opts: {
  trackedName: string;
  persistKey: string;
}): WizardBoundStore {
  return createTrackedStore<SuggestOutcomeWizardState>(opts.trackedName)(persist(set => ({
    ...INITIAL,
    setStep: (step: WizardStep): void => {
      set({
        step
      });
    },
    setLabel: (label: string): void => {
      set({
        label
      });
    },
    setTicker: (ticker: string): void => {
      set({
        ticker
      });
    },
    setImage: (imageUrl: string, imageQuery: string | null): void => {
      set({
        imageUrl,
        imageQuery
      });
    },
    removeImage: (): void => {
      set({
        imageUrl: '',
        imageQuery: null
      });
    },
    setReason: (reason: string): void => {
      set({
        reason
      });
    },
    reset: (): void => {
      set({
        ...INITIAL
      });
    }
  }), {
    name: opts.persistKey,
    version: 1,
    // ALLOW-UNKNOWN: zustand persist migrate callback signature requires unknown
    migrate: (persisted: unknown, _oldVersion: number): PersistedState => {
      if (persisted === null || typeof persisted !== 'object') {
        return pickInitial();
      }
      const raw = persisted as Record<string, unknown>;
      // Persisted blob: URLs are dead refs from a prior session — drop
      // them so the user re-uploads / re-generates the image.
      const persistedImageUrl = typeof raw.imageUrl === 'string' ? raw.imageUrl : '';
      const safeImageUrl = persistedImageUrl.startsWith('blob:') ? '' : persistedImageUrl;
      return {
        step: normaliseStep(raw.step),
        label: typeof raw.label === 'string' ? raw.label : '',
        ticker: typeof raw.ticker === 'string' ? raw.ticker : '',
        imageUrl: safeImageUrl,
        imageQuery: typeof raw.imageQuery === 'string' ? raw.imageQuery : null,
        reason: typeof raw.reason === 'string' ? raw.reason : ''
      };
    },
    partialize: (state: SuggestOutcomeWizardState): PersistedState => ({
      step: state.step,
      label: state.label,
      ticker: state.ticker,
      imageUrl: state.imageUrl,
      imageQuery: state.imageQuery,
      reason: state.reason
    })
  }));
}
function pickInitial(): PersistedState {
  return {
    step: INITIAL.step,
    label: INITIAL.label,
    ticker: INITIAL.ticker,
    imageUrl: INITIAL.imageUrl,
    imageQuery: INITIAL.imageQuery,
    reason: INITIAL.reason
  };
}

// ALLOW-UNKNOWN: Zustand persist migrate hands the rehydrated step in as an untyped opaque blob.
function normaliseStep(raw: unknown): WizardStep {
  if (raw === 1 || raw === 2) return raw;
  return 1;
}

/** Manual flow draft — persists under `morfi-suggest-outcome:manual`. */
export const manualSuggestOutcomeStore: WizardBoundStore = createSuggestOutcomeWizardStore({
  trackedName: 'useSuggestOutcomeWizardStore:manual',
  persistKey: 'morfi-suggest-outcome:manual'
});

/** Mutable mirror of the active provider's store. Kept in sync by
 *  `<SuggestOutcomeWizardStoreProvider>`. Used by static accessors
 *  (`useSuggestOutcomeWizardStore.getState()` etc.) that run outside
 *  React render and so cannot read context. */
let activeStore: WizardBoundStore = manualSuggestOutcomeStore;
const StoreContext: Context<WizardBoundStore> = createContext<WizardBoundStore>(manualSuggestOutcomeStore);
export function SuggestOutcomeWizardStoreProvider({
  children
}: {
  children: ReactNode;
}): JSX.Element {
  if (activeStore !== manualSuggestOutcomeStore) activeStore = manualSuggestOutcomeStore;
  // `.ts` (not `.tsx`) — equivalent to <StoreContext.Provider value={manualSuggestOutcomeStore}>.
  return createElement(StoreContext.Provider, {
    value: manualSuggestOutcomeStore
  }, children);
}
const passThroughSelector = (s: SuggestOutcomeWizardState): SuggestOutcomeWizardState => s;
function useSuggestOutcomeWizardStoreInner(): SuggestOutcomeWizardState;
function useSuggestOutcomeWizardStoreInner<T>(selector: (state: SuggestOutcomeWizardState) => T): T;
function useSuggestOutcomeWizardStoreInner(selector) {
  const store = useContext(StoreContext);
  return useStore(store, (selector ?? passThroughSelector) as (s: SuggestOutcomeWizardState) => T);
}
type UseSuggestOutcomeWizardStore = typeof useSuggestOutcomeWizardStoreInner & {
  getState: () => SuggestOutcomeWizardState;
  setState: WizardBoundStore['setState'];
  subscribe: WizardBoundStore['subscribe'];
};
export const useSuggestOutcomeWizardStore: UseSuggestOutcomeWizardStore = Object.assign(useSuggestOutcomeWizardStoreInner, {
  getState: (): SuggestOutcomeWizardState => activeStore.getState(),
  setState: ((...args: Parameters<WizardBoundStore['setState']>) => activeStore.setState(...args)) as WizardBoundStore['setState'],
  subscribe: ((...args: Parameters<WizardBoundStore['subscribe']>) => activeStore.subscribe(...args)) as WizardBoundStore['subscribe']
});